import { useMemo, useState } from 'react'
import { MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { useDeletePack } from '@/hooks/useBanking'
import { usePacksPage } from '@/hooks/usePacksPage'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import { AddPackDialog } from '@/components/banking/AddPackDialog'
import { EditPackDialog } from '@/components/banking/EditPackDialog'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { useTablePagination } from '@/hooks/useTablePagination'
import type { PackWithMemberNames } from '@/lib/db-tenant'
import type { Pack } from '@/types/banking'
import {
  Button,
  DataTable,
  type DataTableColumn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  toast,
} from '@atrvd/saas-ui'

const NO_PACKS: PackWithMemberNames[] = []

/** Max facility names shown inline in the table before collapsing to "+N more". */
const MAX_VISIBLE_MEMBERS = 3

/**
 * Render the member-facilities cell: the first {@link MAX_VISIBLE_MEMBERS} names
 * joined, then a "+N more" suffix so a pack with many facilities (e.g. 30) can't
 * blow out the table layout. Returns '-' when the pack has no members.
 */
function formatMemberNames(names: string[]): string {
  if (names.length === 0) return '-'
  if (names.length <= MAX_VISIBLE_MEMBERS) return names.join(', ')
  return `${names.slice(0, MAX_VISIBLE_MEMBERS).join(', ')} +${names.length - MAX_VISIBLE_MEMBERS} more`
}

/**
 * Settings page showing the list of Packs in a table with Add / Edit / Delete
 * row actions (DCP-171).
 *
 * Mirrors SettingsFacilitiesPage in structure: PageHeader + DataTable with its
 * pagination footer + action dialogs rendered at the bottom of the component tree.
 *
 * Paged on the server; each pack arrives with its member facilities' names
 * (inactive members included — the column displays membership, it offers no
 * choice, and EditPackDialog lists them too).
 */
export function SettingsPacksPage() {
  // Add dialog state — open flag owned by the page; dialog owns its form.
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  // Edit dialog state — open flag + currently-edited pack. `editingPack` is
  // cleared on close so the form doesn't flash stale data on the next open.
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingPack, setEditingPack] = useState<Pack | null>(null)

  // Delete confirmation state — `deletingPack` drives both the open flag and
  // the confirmation message. Cleared after confirm or cancel.
  const [deletingPack, setDeletingPack] = useState<Pack | null>(null)

  const deletePack = useDeletePack()
  // The total comes back with the page, so the footer — and the move back a
  // page when the only pack of the last page is deleted — go through `forTotal`.
  const pagination = useTablePagination({ totalItems: undefined })
  const packsQuery = usePacksPage({
    page: pagination.page,
    pageSize: pagination.pageSize,
  })
  const { data } = packsQuery
  const { dataTablePagination } = pagination.forTotal({
    total: data?.total,
    isFetching: packsQuery.isFetching,
  })
  const packs = data?.items ?? NO_PACKS

  const columns = useMemo<DataTableColumn<PackWithMemberNames>[]>(
    () => [
      {
        key: 'name',
        header: 'Pack Name',
        render: (pack) => pack.name,
        cellClassName: 'font-medium',
      },
      {
        key: 'facilities',
        header: 'Facilities',
        cellClassName: 'text-muted-foreground',
        // `title` carries the full member list so it stays accessible on hover
        // even when the cell collapses to "+N more".
        render: (pack) => (
          <span title={pack.memberNames.join(', ') || undefined}>{formatMemberNames(pack.memberNames)}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: '50px',
        render: (pack) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setEditingPack({ id: pack.id, name: pack.name })
                  setEditDialogOpen(true)
                }}
              >
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDeletingPack({ id: pack.id, name: pack.name })}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // Only state setters are captured, so the columns never need rebuilding.
    [],
  )

  /**
   * Confirm deletion of the selected pack. Closes the dialog only on success;
   * on failure it stays open (with an error toast) so the user can retry without
   * re-opening the menu — consistent with the Add/Edit dialogs' error handling.
   * (useDeletePack has no onError, so the error must be surfaced here.)
   */
  const handleDeleteConfirm = async () => {
    if (!deletingPack) return
    try {
      await deletePack.mutateAsync(deletingPack.id)
      toast.success('Pack deleted')
      setDeletingPack(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete pack')
    }
  }

  const tableState = tableQueryState(packsQuery, packs.length)
  const errorDisplay = queryErrorDisplay(tableState, packs.length)

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Packs (${data?.total ?? 0})`}
        description="Manage facility packs"
        actions={
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Pack
          </Button>
        }
      />

      {errorDisplay.banner}
      <DataTable
        columns={columns}
        data={packs}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        error={errorDisplay.tableError}
        rowKey={(pack) => pack.id}
        itemName="packs"
        emptyState={<p className="text-center text-muted-foreground">No packs found</p>}
        // No footer up to 20 packs (lib/pagination.ts).
        pagination={dataTablePagination}
      />

      {/* Add Pack Dialog — owned add form, reset on open */}
      <AddPackDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      {/* Edit Pack Dialog — seeded from editingPack, cleared on close */}
      <EditPackDialog
        open={editDialogOpen}
        onOpenChange={(o) => {
          setEditDialogOpen(o)
          // Clear the selected pack on close so the next open starts fresh.
          if (!o) setEditingPack(null)
        }}
        pack={editingPack}
      />

      {/* Delete Confirmation Dialog — mirrors SettingsCategoriesPage pattern */}
      <ConfirmationDialog
        open={!!deletingPack}
        onOpenChange={(open: boolean) => !open && !deletePack.isPending && setDeletingPack(null)}
        title="Delete Pack"
        description={`Are you sure you want to delete "${deletingPack?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        isLoading={deletePack.isPending}
        variant="destructive"
      />
    </div>
  )
}
